import React, { useEffect, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { uniq } from 'lodash';
import { differenceInDays, parseISO } from 'date-fns';
import { LOGGEDIN_LINKS } from '@web/molecules';
import {
  Hero,
  Footer,
  LoadingView,
  ConfirmationBanner,
  DropdownConfirmationBanner,
  ConfirmationPoll,
  GetSocial,
} from '@web/components';
import { useToast, TOAST_CLIENT_ERROR_MESSAGE } from '@web/Toast';
import { usePets } from '../../pets';
import { useSubscription, useSubscriptions } from '../../subscriptions';
import {
  determinePlanType,
  getVariantByRecipes,
  useProductRecommendations,
  useProductsList,
  getCurrentMealPlanTypeOptions,
} from '../../products';
import { useSubscriptionAddonProductsList } from '../../addons';
import { useReporter } from '../../reporter';
import { SlimConfirmationOrderSummary } from '../SlimConfirmationOrderSummary';
import { useOrders, useOrderList } from '../../orders';
import {
  SHOW_BOX_FREQUENCY_BANNER,
  SHOW_CONFIRMATION_HERO_APP_CTA,
  useExperiment,
} from '../../experiments';
import { useSessionCustomer } from '../../customer';
import { AccountHeader } from '../../account/AccountHeader';
import { FOOTER_CONTENT } from '../../testing/constants';
import { RedirectToDashboard } from '../routing';
import { ApiResult } from '../../api';
import { OnboardingContext } from '../OnboardingFlowContainer/context';
import {
  calculateGrandTotals,
  formatDate,
  formatOrderSummary,
  getWebConfirmationOrderSummaryProps,
  getOptionCallout,
} from './dataParsers';
import {
  FREQUENCY_ERROR_MESSAGE,
  FREQUENCY_SAVED_TEXT,
  FREQUENCY_SAVED_DETAILS_TEXT,
} from './constants';
import {
  userViewsCheckoutConfirmation,
  userClickedViewAccount,
} from './events';
import {
  StyledCheckoutConfirmationContainer,
  Container,
  LeftSideWrapper,
  RightSideWrapper,
  MobileGetSocialWrapper,
  DesktopGetSocialWrapper,
  HeroContainer,
} from './styles';

export const CheckoutConfirmationContainer = ({
  hero,
  leftSideComponents,
  rightSideComponents,
  orderSummaryTitle,
  frequencyCalloutsList,
}) => {
  const [frequencySaved, setFrequencySaved] = useState(false);
  const { showToast } = useToast();
  const { value: showBoxFrequencyBanner } = useExperiment(
    SHOW_BOX_FREQUENCY_BANNER,
    false,
  );
  const { value: showConfirmationHeroAppCTA } = useExperiment(
    SHOW_CONFIRMATION_HERO_APP_CTA,
    false,
  );
  const { offer } = useContext(OnboardingContext);
  const pricePeriod = 'week';
  const { customer, isLoading: isCustomerLoading } = useSessionCustomer();
  const { subscriptions, isLoading: isSubscriptionsLoading } =
    useSubscriptions();
  const { pets, isLoading: isPetsLoading } = usePets({ hasSubscription: true });
  const { products, isLoading: isProductsLoading } = useProductsList(
    !isSubscriptionsLoading
      ? uniq(subscriptions.map(subscription => subscription.sku))
      : null,
  );
  const { data: addons, isLoading: isAddonsLoading } =
    useSubscriptionAddonProductsList();
  const { orders, isLoading: isOrdersLoading } = useOrders();
  const orderIds = orders?.map(order => order.id);
  const { orders: orderList, isLoading: isOrderListLoading } =
    useOrderList(orderIds);

  const showFrequencyDropdown = pets?.length === 1 && showBoxFrequencyBanner;
  const {
    fresh,
    dry,
    mixed,
    half_fresh,
    isLoading: isRecommendationsLoading,
  } = useProductRecommendations({
    petId: showFrequencyDropdown ? pets?.[0]?.id : null,
  });

  const { updateSubscription } = useSubscription(pets?.[0]?.id);

  const isLoading =
    isSubscriptionsLoading ||
    isPetsLoading ||
    isProductsLoading ||
    isAddonsLoading ||
    isOrdersLoading ||
    isOrderListLoading ||
    isCustomerLoading ||
    isRecommendationsLoading;

  const product = products?.[0];
  const mealPlanType = getCurrentMealPlanTypeOptions({
    product,
    fresh,
    dry,
    mixed,
    half_fresh,
  });

  const frequencyCallouts = frequencyCalloutsList?.map(callout => callout.body);

  const frequencyChoices = useMemo(
    () =>
      isLoading || !showFrequencyDropdown
        ? []
        : mealPlanType
            .map((plan, i) => {
              const recipes = {
                fresh: product?.variants[0].recipes.fresh,
                dry: product?.variants[0].recipes.dry,
              };
              const variant = getVariantByRecipes(plan.variants, recipes);

              const planType = determinePlanType(recipes);

              const titles = {
                mixed: `$${variant.price_per_week} per week (${variant.tray_count} packs, ${variant.bag_count} bags)`,
                fresh: `$${variant.price_per_week} per week (${variant.tray_count} packs)`,
                baked: `$${variant.price_per_week} per week (${variant.bag_count} bags)`,
              };
              const optionCallout = getOptionCallout({
                frequencyCallouts,
                frequencies: mealPlanType,
                i,
              });
              return {
                sku: variant.sku,
                value: plan.weeks_of_food,
                label:
                  plan.weeks_of_food > 1
                    ? `Every ${plan.weeks_of_food} Weeks ${optionCallout}`
                    : `Every week (${frequencyCallouts[0]})`,
                details: titles[planType],
              };
            })
            .sort((a, b) => (a.value > b.value ? 1 : -1)),
    [
      isLoading,
      showFrequencyDropdown,
      mealPlanType,
      product,
      frequencyCallouts,
    ],
  );

  const defaultFrequencyOption = !!frequencyChoices?.find(
    choice => choice?.value === 4,
  )
    ? 4
    : frequencyChoices[frequencyChoices.length - 1]?.value;

  const onSubmit = async value => {
    const selectedFrequency = frequencyChoices?.find(
      choice => choice.value === value,
    );
    const result = await updateSubscription({
      frequency: selectedFrequency.value,
      sku: selectedFrequency.sku,
      pet_id: pets?.[0]?.id,
    });
    ApiResult.match(result, {
      success: () => {
        setFrequencySaved(true);
      },
      error: {
        server: () => {
          showToast({
            status: 'error',
            message: FREQUENCY_ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
        client: error => {
          reporter.error(error.original);
          showToast({
            status: 'error',
            message: TOAST_CLIENT_ERROR_MESSAGE,
            headline: 'Error Message',
          });
        },
      },
    });
  };

  const reporter = useReporter();
  useEffect(() => {
    if (!isLoading) {
      reporter.tag(userViewsCheckoutConfirmation());
    }
  }, [reporter, isLoading]);

  if (isLoading) {
    return <LoadingView />;
  }

  const firstOrderDate = () => {
    return (
      orders?.reduce((oldest, order) => {
        return oldest < order.date_created ? oldest : order.date_created;
      }, orders[0].date_created) ?? null
    );
  };

  // Redirect to dashboard if order is >= 1 day ago
  if (
    !isLoading &&
    differenceInDays(new Date(), parseISO(firstOrderDate())) >= 1
  ) {
    return (
      <RedirectToDashboard pet={pets[0]}>
        <LoadingView />
      </RedirectToDashboard>
    );
  }

  const ordersSummary = formatOrderSummary(
    pets,
    subscriptions,
    orderList,
    products,
    addons,
    pricePeriod,
  );

  const petNames = [];
  for (let count = 0; count < pets.length; count++) {
    petNames.push(pets[count]?.name);
  }

  const getListOfNames = () => {
    let listOfNames = '';

    const lastPetName = petNames.pop();

    if (!petNames?.length) {
      listOfNames = `${lastPetName}'s`;
    } else {
      listOfNames = `${petNames.join(', ')}, and ${lastPetName}'s`;
    }

    return listOfNames;
  };

  // TODO: Get this from the backend (both price total and after discount)
  const { subtotal, totalTax, totalPrice } = calculateGrandTotals(orderList);
  const deliveryDate = formatDate(orderList[0].delivery_date);

  const confirmationHeroProps = showConfirmationHeroAppCTA
    ? {
        headline: hero.headlineVariants[0].heading,
        subheadline: hero.subheadline,
        image: hero.image,
        backgroundColor: hero.backgroundColor,
        fontColor: hero.fontColor,
        footer: (
          <p>
            Your pup's starter box is estimated to arrive on{' '}
            <strong>{deliveryDate}</strong>
          </p>
        ),
        horizontalOrientation: 'center',
        divider: true,
        socialButtons: hero.socialButtons,
      }
    : {
        headline: hero.headline,
        subheadline: `${getListOfNames()} starter box is estimated to arrive on ${deliveryDate}`,
        cta: hero.cta,
        ctaColor: hero.ctaColor,
        image: hero.image,
        backgroundColor: hero.backgroundColor,
        fontColor: hero.fontColor,
      };

  const { formattedPricingList, plans } = getWebConfirmationOrderSummaryProps(
    offer,
    orderList,
    pets,
    products,
  );

  const confirmationOrderSummaryProps = {
    orders: ordersSummary,
    orderTotalCost: totalPrice,
    orderTotalTax: totalTax,
    orderSubtotal: subtotal,
    orderSummaryTitle,
    period: pricePeriod,
    pricingList: formattedPricingList,
    plans: plans,
  };
  const freeItems = rightSideComponents?.find(
    item => item.component === 'free_items',
  );

  return (
    <StyledCheckoutConfirmationContainer>
      <AccountHeader
        links={LOGGEDIN_LINKS}
        logoUrl={'/'}
        logoSrText={'Ollie Logo'}
        name={customer.first_name}
      />
      <HeroContainer>
        <Hero {...confirmationHeroProps} />
      </HeroContainer>
      <Container>
        <LeftSideWrapper>
          {leftSideComponents?.map((item, key) => {
            const showFrequencyDropdownComponent =
              showFrequencyDropdown && item?.dropdown === 'frequency';
            return item.component === 'confirmation_banner' &&
              showFrequencyDropdownComponent ? (
              <DropdownConfirmationBanner
                key={key}
                {...item}
                defaultValues={{ frequency: defaultFrequencyOption }}
                dropdownName={item?.dropdown}
                options={frequencyChoices}
                savedText={FREQUENCY_SAVED_TEXT}
                savedDetails={FREQUENCY_SAVED_DETAILS_TEXT}
                optionSaved={frequencySaved}
                onSubmit={onSubmit}
                buttonText={'Save frequency'}
              />
            ) : item.component === 'confirmation_banner' ? (
              <ConfirmationBanner key={key} {...item} />
            ) : (
              item.component === 'confirmation_poll' && (
                <ConfirmationPoll key={key} {...item} />
              )
            );
          })}
        </LeftSideWrapper>
        {rightSideComponents ? (
          <RightSideWrapper>
            <SlimConfirmationOrderSummary
              {...confirmationOrderSummaryProps}
              freeItems={freeItems?.items}
              onClickAccount={() => reporter.tag(userClickedViewAccount())}
            />
            <DesktopGetSocialWrapper>
              {rightSideComponents.map(
                (item, key) =>
                  item.component === 'get_social_component' && (
                    <GetSocial key={key} {...item} />
                  ),
              )}
            </DesktopGetSocialWrapper>
          </RightSideWrapper>
        ) : (
          <SlimConfirmationOrderSummary
            {...confirmationOrderSummaryProps}
            onClickAccount={() => reporter.tag(userClickedViewAccount())}
          />
        )}
        {rightSideComponents && (
          <MobileGetSocialWrapper>
            {rightSideComponents.map(
              (item, key) =>
                item.component === 'get_social_component' && (
                  <GetSocial key={key} {...item} />
                ),
            )}
          </MobileGetSocialWrapper>
        )}
      </Container>
      <Footer {...FOOTER_CONTENT} />
    </StyledCheckoutConfirmationContainer>
  );
};

CheckoutConfirmationContainer.displayName = 'CheckoutConfirmation';
CheckoutConfirmationContainer.propTypes = {
  headline: PropTypes.string.isRequired,
  hero: PropTypes.object.isRequired,
  orderSummaryTitle: PropTypes.string.isRequired,
  leftSideComponents: PropTypes.array.isRequired,
  rightSideComponents: PropTypes.array.isRequired,
  frequencyCalloutsList: PropTypes.array.isRequired,
};
